import type { ReactNode } from 'react';

import { useLocation, Link as RouterLink } from 'react-router';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';

import { paths } from 'src/routes/paths';

import { useAdminLogout } from 'src/hooks/useAdminAuth';

import { fonts } from 'src/theme';
import LtaLogo from 'src/assets/svgs/LtaLogo';

const NAV_ITEMS = [
  {
    label: 'Activities',
    to: paths.admin.activities,
    match: '/admin/activities',
    icon: <VolunteerActivismOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Careers',
    to: paths.admin.careers,
    match: '/admin/careers',
    icon: <WorkOutlineOutlinedIcon fontSize="small" />,
  },
];

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { logout } = useAdminLogout();
  const { pathname } = useLocation();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'common.white' }}>
      <Box
        component="a"
        href="#admin-main"
        sx={{
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: (theme) => theme.zIndex.tooltip,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          bgcolor: 'background.paper',
          color: 'primary.main',
          fontWeight: 700,
          transform: 'translateY(-140%)',
          transition: 'transform .15s ease',
          '&:focus': {
            transform: 'translateY(0)',
          },
        }}
      >
        Skip to content
      </Box>
      <AppBar
        position="sticky"
        color="primary"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: alpha('#fff', 0.18),
          boxShadow: '0 10px 30px rgba(0, 64, 110, 0.12)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 60, gap: { xs: 0.5, sm: 1 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mr: { xs: 1, sm: 2.5 },
                px: 0.75,
                py: 0.5,
                borderRadius: 1,
                bgcolor: alpha('#fff', 0.08),
              }}
            >
              <LtaLogo height={22} width={36} />
              <Typography
                variant="h6"
                sx={{ fontFamily: fonts.body, fontWeight: 600, fontSize: 24, lineHeight: 1 }}
              >
                CMS
              </Typography>
            </Box>
            <Box component="nav" aria-label="Admin sections" sx={{ display: 'flex', gap: 1 }}>
              {NAV_ITEMS.map((item) => {
                const isActive = pathname.startsWith(item.match);
                return (
                  <Button
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={item.label}
                    startIcon={item.icon}
                    sx={{
                      color: 'common.white',
                      fontSize: 16,
                      fontWeight: isActive ? 700 : 500,
                      opacity: isActive ? 1 : 0.85,
                      borderRadius: 1,
                      minHeight: 38,
                      minWidth: { xs: 38, md: 'auto' },
                      px: { xs: 1, md: 1.5 },
                      bgcolor: isActive ? alpha('#fff', 0.16) : 'transparent',
                      '&:hover': { bgcolor: alpha('#fff', isActive ? 0.22 : 0.1) },
                      '& .MuiButton-startIcon': { mr: { xs: 0, md: 1 } },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                      {item.label}
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={logout}
              variant="outlined"
              aria-label="Log out"
              startIcon={<LogoutOutlinedIcon fontSize="small" />}
              sx={{
                color: 'common.white',
                fontSize: 16,
                borderColor: alpha('#fff', 0.6),
                borderRadius: 1,
                minHeight: 38,
                minWidth: { xs: 38, md: 'auto' },
                px: { xs: 1, md: 2 },
                '& .MuiButton-startIcon': { mr: { xs: 0, md: 1 } },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                Log out
              </Box>
            </Button>
          </Toolbar>
        </Container>
      </AppBar>
      <Container id="admin-main" component="main" maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
        {children}
      </Container>
    </Box>
  );
}
